import { LockIcon, PlusIcon } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { useLocation, useNavigate } from "react-router"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { useShownAfter } from "./page-skeleton.tsx"
import { currentAnchor } from "./settings.ts"

/*
 * The parts of a settings page: titled sections, each holding a panel of rows, and an AnchorNav
 * beside them that follows the Member down the page. Project settings and the Agent page are built
 * from these, so every setting in the app sits in the same frame: a label column on the left, the
 * control on the right, a hairline between rows.
 */

/**
 * One titled section of a settings page, `id` being its anchor. `description` says what the
 * section is for, under the title; `action` is the section's own control, at the end of its header,
 * such as "Give access". `locked` marks a section the Member can read but not change.
 */
export function SettingsSection({
  id,
  title,
  description,
  action,
  locked = false,
  children,
}: {
  id: string
  title: string
  description?: string
  action?: ReactNode
  locked?: boolean
  children: ReactNode
}) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-16">
      <div className="mb-3 flex items-end gap-4">
        <div className="min-w-0 space-y-0.5">
          <h2 id={`${id}-title`} className="flex items-center gap-1.5 text-title font-semibold">
            {title}
            {locked && (
              <Tooltip>
                <TooltipTrigger render={<span className="inline-flex" />}>
                  <LockIcon
                    role="img"
                    aria-label="Read only"
                    className="size-3.5 text-subtle-foreground"
                  />
                </TooltipTrigger>
                <TooltipContent>Read only</TooltipContent>
              </Tooltip>
            )}
          </h2>
          {description && (
            <p className="max-w-prose text-ui text-muted-foreground">{description}</p>
          )}
        </div>
        {action && <div className="ml-auto flex shrink-0 items-center gap-2">{action}</div>}
      </div>
      {children}
    </section>
  )
}

const panelTones = {
  default: "ring-border",
  danger: "ring-destructive/25",
} as const

/**
 * The card a section's rows rest on, a hairline between each. `danger` rims it in red, for the
 * section whose actions can't be taken back. Its first and last rows take its rounded corners, so a
 * row's hover fill stays inside them.
 */
export function SettingsPanel({
  tone = "default",
  children,
}: {
  tone?: keyof typeof panelTones
  children: ReactNode
}) {
  return (
    <div
      data-slot="settings-panel"
      className={cn(
        "divide-y divide-border-subtle rounded-lg bg-card shadow-raised ring-1 *:first:rounded-t-lg *:last:rounded-b-lg",
        panelTones[tone],
      )}
    >
      {children}
    </div>
  )
}

/*
 * A list row's height: `default` for a line of text and controls, `tall` for one led by a 24px
 * avatar or tile, `auto` for one whose content wraps onto more lines, such as a Slack rule and its
 * events.
 */
const listHeights = {
  default: "h-10",
  tall: "h-11",
  auto: "min-h-10 py-2.5",
} as const

/*
 * How an inline row shares its width. A `field` (a select, a switch, an input) sits right of a
 * label column of fixed width, so the controls of a panel's rows line up. A `button`, the row's one
 * action, takes only its own width at the end and leaves the rest to the label and its helper,
 * which would otherwise wrap into a narrow column beside a wide gap.
 */
const inlineHeadings = {
  field: "w-60 shrink-0",
  button: "min-w-0 flex-1",
} as const

const inlineControls = {
  field: "flex min-w-0 flex-1 justify-end",
  button: "ml-auto flex shrink-0",
} as const

/**
 * One row of a settings panel, in one of three layouts. `inline` puts the `label` and its `helper`
 * in a column on the left and the control on the right; `stacked` puts the label above a control
 * that needs the width, such as a description; `list` is a row of a list (a grant, a Status, a
 * Slack rule) whose children compose it, its actions revealed on hover (`group/row`). `htmlFor`
 * ties the label to the control it names. `control` is what an inline row holds: a `field` (the
 * default) or a single `button`, as the danger zone's rows do.
 */
export function SettingsRow({
  label,
  helper,
  htmlFor,
  layout,
  height = "default",
  control = "field",
  children,
}: {
  label?: string
  helper?: ReactNode
  htmlFor?: string
  layout: "inline" | "stacked" | "list"
  /** A `list` row's height. */
  height?: keyof typeof listHeights
  /** What an `inline` row's control is, which decides how the row shares its width. */
  control?: keyof typeof inlineHeadings
  children: ReactNode
}) {
  if (layout === "list")
    return (
      <div
        className={cn(
          "group/row flex items-center gap-3 px-4 transition-colors duration-100 ease-snappy hover:bg-accent",
          listHeights[height],
        )}
      >
        {children}
      </div>
    )

  const heading = (label || helper) && (
    <div className={cn("space-y-0.5", layout === "inline" && inlineHeadings[control])}>
      {label &&
        (htmlFor ? (
          <label htmlFor={htmlFor} className="block text-ui font-medium">
            {label}
          </label>
        ) : (
          <p className="text-ui font-medium">{label}</p>
        ))}
      {helper && <div className="text-xs text-muted-foreground">{helper}</div>}
    </div>
  )

  if (layout === "stacked")
    return (
      <div className="space-y-2 px-4 py-3">
        {heading}
        {children}
      </div>
    )

  return (
    <div className="flex min-h-12 items-center gap-6 px-4 py-3">
      {heading}
      <div className={inlineControls[control]}>{children}</div>
    </div>
  )
}

/**
 * The last row of a list that grows, such as "Add Status". It expands in place: the caller renders
 * the row's form where it stood.
 */
export function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 w-full items-center gap-2 px-4 text-ui text-muted-foreground transition-colors duration-100 ease-snappy outline-none hover:bg-accent hover:text-foreground focus-visible:bg-accent focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <PlusIcon aria-hidden className="size-4" />
      {label}
    </button>
  )
}

/** The page's one scroll container, which the shell marks, or the viewport when there is none. */
function scrollRootOf(element: Element | null) {
  return element?.closest<HTMLElement>("[data-slot=page-scroll]") ?? null
}

function isAtBottom(root: HTMLElement | null) {
  if (root) return root.scrollTop + root.clientHeight >= root.scrollHeight - 2
  return window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2
}

// The band a section is read in: from just under the 48px PageHeader to 45% of the way down.
const readingBand = "-56px 0px -55% 0px"

// How long, at most, arriving at a section keeps it in place while the sections above it load.
const settlingFor = 5000

// The keys that scroll the page, or move the focus along it, which the Member presses themselves.
const scrollKeys = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
  "Tab",
])

/**
 * A settings page's table of contents, in its `nav` pane. The section being read is marked as the
 * Member scrolls; a click scrolls smoothly to its section and puts it in the address (#statuses), so
 * the address can be shared. Arriving with one, the page opens at that section.
 *
 * A section chosen by a click, or by arriving at it, stays marked until the Member scrolls on their
 * own, even when it is too short to reach the top of the page and the page stops below it.
 */
export function AnchorNav({ items }: { items: readonly { id: string; label: string }[] }) {
  const nav = useRef<HTMLElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const ids = items.map(({ id }) => id)
  const key = ids.join(" ")
  const hashed = location.hash.slice(1)
  const [current, setCurrent] = useState<string | undefined>(
    ids.includes(hashed) ? hashed : undefined,
  )
  // The section the Member chose, by a click or the address. While its scroll runs, the sections
  // it passes are not the ones being read, and once it ends, the chosen one is.
  const clicked = useRef<{ id: string } | undefined>(undefined)

  useEffect(() => {
    const ids = key ? key.split(" ") : []
    const root = scrollRootOf(nav.current)
    const showing = new Set<string>()
    const follow = () => {
      const click = clicked.current
      if (click) return setCurrent(click.id)
      setCurrent((previous) => currentAnchor(ids, showing, isAtBottom(root), previous))
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries)
          if (entry.isIntersecting) showing.add(entry.target.id)
          else showing.delete(entry.target.id)
        follow()
      },
      { root, rootMargin: readingBand },
    )
    for (const id of ids) {
      const section = document.getElementById(id)
      if (section) observer.observe(section)
    }
    const scroller: HTMLElement | Window = root ?? window
    // The Member scrolling on their own, by wheel, touch, key or the scrollbar, lets go of the
    // section they chose. A press anywhere else, such as on a button in that section, doesn't.
    const release = () => {
      if (!clicked.current) return
      clicked.current = undefined
      follow()
    }
    const releaseByKey = (event: KeyboardEvent) => {
      if (scrollKeys.has(event.key)) release()
    }
    const releaseByScrollbar = (event: PointerEvent) => {
      if (event.target === (root ?? document.documentElement)) release()
    }
    scroller.addEventListener("scroll", follow, { passive: true })
    scroller.addEventListener("wheel", release, { passive: true })
    scroller.addEventListener("touchmove", release, { passive: true })
    window.addEventListener("pointerdown", releaseByScrollbar)
    window.addEventListener("keydown", releaseByKey)
    return () => {
      observer.disconnect()
      scroller.removeEventListener("scroll", follow)
      scroller.removeEventListener("wheel", release)
      scroller.removeEventListener("touchmove", release)
      window.removeEventListener("pointerdown", releaseByScrollbar)
      window.removeEventListener("keydown", releaseByKey)
    }
  }, [key])

  // Arriving at #statuses opens the page there, once its section is on the page, and keeps it there
  // while the sections above it load and grow, until the Member scrolls on their own.
  // The address the page was opened at; a click's own #anchor later is not an arrival.
  const arrivedAt = useRef(hashed)
  const arrived = useRef(false)
  useEffect(() => {
    const id = arrivedAt.current
    if (arrived.current || !key.split(" ").includes(id)) return
    const section = document.getElementById(id)
    if (!section) return
    arrived.current = true
    clicked.current = { id }
    let settling = true
    const place = () => {
      if (clicked.current?.id === id) section.scrollIntoView({ block: "start" })
    }
    place()
    const growing = new ResizeObserver(place)
    growing.observe(section.parentElement ?? section)
    const settled = setTimeout(() => {
      settling = false
      growing.disconnect()
    }, settlingFor)
    return () => {
      growing.disconnect()
      clearTimeout(settled)
      // Undone before it settled (as a development build does, running each effect twice), the
      // arrival starts over when the effect runs again.
      if (settling && clicked.current?.id === id) arrived.current = false
    }
  }, [key])

  function go(id: string) {
    const section = document.getElementById(id)
    if (!section) return
    clicked.current = { id }
    setCurrent(id)
    // A script's smooth scroll is not CSS's, so the reduced-motion rule in tokens.css can't flatten
    // it: ask the machine here.
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches
    section.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" })
    void navigate(
      { pathname: location.pathname, search: location.search, hash: `#${id}` },
      { replace: true, preventScrollReset: true, state: location.state },
    )
  }

  return (
    <nav ref={nav} aria-label="On this page" className="flex flex-col gap-0.5">
      {items.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          aria-current={id === current ? "location" : undefined}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
            event.preventDefault()
            go(id)
          }}
          className={cn(
            "flex h-7 items-center rounded-md px-2 text-ui transition-colors duration-100 ease-snappy outline-none focus-visible:ring-2 focus-visible:ring-ring",
            id === current
              ? "bg-selected font-medium text-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          {label}
        </a>
      ))}
    </nav>
  )
}

/** Label widths, fixed so the rows read as varied names yet never move between renders. */
const widths = ["w-40", "w-28", "w-36", "w-24"] as const

/**
 * A section's panel while its list loads, on any settings page: skeleton rows at the height of the rows that will
 * replace them, `tall` for rows led by an avatar. Like a PageSkeleton it shows only once the load
 * has run a moment, but it holds its place meanwhile, so the sections under it never move.
 */
export function SectionSkeleton({ rows, height }: { rows: number; height: "default" | "tall" }) {
  const shown = useShownAfter()
  return (
    <div role="status" aria-busy aria-label="Loading">
      <div className={shown ? "animate-in duration-150 fade-in" : "invisible"}>
        <SettingsPanel>
          {widths.slice(0, rows).map((width) => (
            <SettingsRow key={width} layout="list" height={height}>
              <Skeleton className={cn("rounded-full", height === "tall" ? "size-6" : "size-4")} />
              <Skeleton className={cn("h-3", width)} />
              <Skeleton className="ml-auto h-3 w-16" />
            </SettingsRow>
          ))}
        </SettingsPanel>
      </div>
    </div>
  )
}
